import * as React from 'react';
import gettext from '@sdk/lang/gettext';
import Picker from '@sdk/components/forms/Picker';
import EditablePicker from '@sdk/components/forms/EditablePicker';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import useAuth from '@sdk/authentication/useAuth';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { TIMECLOCK_NOTIFICATIONS } from '@sdk/common/constants';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';

import { TabType } from '../../types';
import RowSpaceBetween from '../RowSpaceBetween';
import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import SettingsSection from '../SettingsSection';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import {
  CLOCK_NOTIFICATIONS_MINUTES_OPTIONS,
  CLOCK_IN_NOTIFICATIONS_OPTIONS,
  CLOCK_OUT_NOTIFICATIONS_OPTIONS,
  FIELD_WIDTH_205,
  FIELD_WIDTH_420,
  CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
} from '../../constants';
import { useUserSettings } from '../../UserSettingsContext';

enum NotificationType {
  clockInNotifications = 'clockInNotifications',
  clockOutNotifications = 'clockOutNotifications',
}

enum NotificationsMinutesType {
  clockInNotificationsLateMinutes = 'clockInNotificationsLateMinutes',
  clockOutNotificationsLateMinutes = 'clockOutNotificationsLateMinutes',
}

enum MissingNotificationsMinutesType {
  missingClockInNotificationsMinutes = 'missingClockInNotificationsMinutes',
  missingClockOutNotificationsMinutes = 'missingClockOutNotificationsMinutes',
}

const TimeclockNotificationManagerSettings: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { toggleError } = useSettingsErrorsContext();
  const { userSettings, onUserSettingsChange } = useUserSettings();
  const { currentUser } = useAuth();
  const {
    timesheetsNotify,
    clockInNotifications,
    clockInNotificationsLateMinutes,
    clockOutNotifications,
    clockOutNotificationsLateMinutes,
  } = userSettings;

  const lateMinutesFor = (minutesProp: NotificationsMinutesType): number => {
    return minutesProp === NotificationsMinutesType.clockInNotificationsLateMinutes
      ? clockInNotificationsLateMinutes
      : clockOutNotificationsLateMinutes;
  };

  const onNotificationChange = (
    value: string,
    notificationProp: NotificationType,
    minutesProp: NotificationsMinutesType,
    missingMinutesProp: MissingNotificationsMinutesType
  ): void => {
    switch (value) {
      case TIMECLOCK_NOTIFICATIONS.ALWAYS:
        onUserSettingsChange({
          [notificationProp]: value,
          [missingMinutesProp]: lateMinutesFor(minutesProp) || CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
        });
        break;
      case TIMECLOCK_NOTIFICATIONS.ONLY_LATE:
        onUserSettingsChange({
          [notificationProp]: value,
          [missingMinutesProp]: lateMinutesFor(minutesProp) || CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
        });
        break;
      case TIMECLOCK_NOTIFICATIONS.NEVER:
        onUserSettingsChange({
          [notificationProp]: value,
          [minutesProp]: CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
          [missingMinutesProp]: -1,
        });
        break;
    }
  };

  return (
    <>
      {currentUser.orgSettings.usersCanEditTheirTimesheets && (
        <SettingsSwitcherRow
          withSeparator
          value={timesheetsNotify}
          onChange={(value): void => onUserSettingsChange({ timesheetsNotify: value })}
          title={gettext('Editing notification')}
          subtitle={gettext('Enable notifications when employees edit their timesheets.')}
        />
      )}

      <SettingsSection withSeparator>
        <CollapsibleSettingsSection
          isActive={clockInNotifications !== TIMECLOCK_NOTIFICATIONS.NEVER}
          headerItem={
            <Column pBottom={20}>
              <RowSpaceBetween>
                <Column>
                  <Text14 semiBold themeColor={ThemeColorType.gray}>
                    {gettext('Clock in')}
                  </Text14>
                  <Text14 themeColor={ThemeColorType.gray}>
                    {gettext('Send dashboard notifications:')}
                  </Text14>
                </Column>
                <Picker
                  options={CLOCK_IN_NOTIFICATIONS_OPTIONS}
                  value={clockInNotifications}
                  onChange={(value: string): void =>
                    onNotificationChange(
                      value,
                      NotificationType.clockInNotifications,
                      NotificationsMinutesType.clockInNotificationsLateMinutes,
                      MissingNotificationsMinutesType.missingClockInNotificationsMinutes
                    )
                  }
                  customWidth={FIELD_WIDTH_420}
                />
              </RowSpaceBetween>
            </Column>
          }
          contentItem={
            <Column>
              <RowSpaceBetween>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Define late in number of minutes after a shift is scheduled to start:')}
                </Text14>
                <EditablePicker
                  options={CLOCK_NOTIFICATIONS_MINUTES_OPTIONS}
                  value={clockInNotificationsLateMinutes}
                  postfix="Minutes"
                  onChange={(value: number): void => {
                    onUserSettingsChange({
                      [NotificationsMinutesType.clockInNotificationsLateMinutes]: Number(value),
                      [MissingNotificationsMinutesType.missingClockInNotificationsMinutes]:
                        Number(value),
                    });
                    toggleError(
                      TabType.notifications,
                      NotificationsMinutesType.clockInNotificationsLateMinutes,
                      !validateNumber(value, {
                        [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
                        [ValidateOptions.validNumberGreaterOrEqualThan]:
                          CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
                        [ValidateOptions.integer]: true,
                      })
                    );
                  }}
                  customWidth={FIELD_WIDTH_205}
                  usePickerValueWhenDisplay
                  invalid={
                    !validateNumber(clockInNotificationsLateMinutes, {
                      [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
                      [ValidateOptions.validNumberGreaterOrEqualThan]:
                        CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
                      [ValidateOptions.integer]: true,
                    })
                  }
                />
              </RowSpaceBetween>
            </Column>
          }
        />
      </SettingsSection>

      <CollapsibleSettingsSection
        isActive={clockOutNotifications !== TIMECLOCK_NOTIFICATIONS.NEVER}
        headerItem={
          <Column pBottom={20}>
            <RowSpaceBetween>
              <Column>
                <Text14 semiBold themeColor={ThemeColorType.gray}>
                  {gettext('Clock out')}
                </Text14>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Send dashboard notifications:')}
                </Text14>
              </Column>
              <Picker
                options={CLOCK_OUT_NOTIFICATIONS_OPTIONS}
                value={clockOutNotifications}
                onChange={(value: string): void =>
                  onNotificationChange(
                    value,
                    NotificationType.clockOutNotifications,
                    NotificationsMinutesType.clockOutNotificationsLateMinutes,
                    MissingNotificationsMinutesType.missingClockOutNotificationsMinutes
                  )
                }
                customWidth={FIELD_WIDTH_420}
              />
            </RowSpaceBetween>
          </Column>
        }
        contentItem={
          <Column>
            <RowSpaceBetween>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('Define late in number of minutes after a shift is scheduled to end:')}
              </Text14>
              <EditablePicker
                options={CLOCK_NOTIFICATIONS_MINUTES_OPTIONS}
                value={clockOutNotificationsLateMinutes}
                postfix="Minutes"
                onChange={(value: number): void => {
                  onUserSettingsChange({
                    [NotificationsMinutesType.clockOutNotificationsLateMinutes]: Number(value),
                    [MissingNotificationsMinutesType.missingClockOutNotificationsMinutes]:
                      Number(value),
                  });
                  toggleError(
                    TabType.notifications,
                    NotificationsMinutesType.clockOutNotificationsLateMinutes,
                    !validateNumber(value, {
                      [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
                      [ValidateOptions.validNumberGreaterOrEqualThan]:
                        CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
                      [ValidateOptions.integer]: true,
                    })
                  );
                }}
                customWidth={FIELD_WIDTH_205}
                usePickerValueWhenDisplay
                invalid={
                  !validateNumber(clockOutNotificationsLateMinutes, {
                    [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
                    [ValidateOptions.validNumberGreaterOrEqualThan]:
                      CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
                    [ValidateOptions.integer]: true,
                  })
                }
              />
            </RowSpaceBetween>
          </Column>
        }
      />
    </>
  );
};

export default TimeclockNotificationManagerSettings;
